import { BannerType, ContentFulScreens, ICustomizedBanner, useAppDispatch, useCms } from "@next-platform/client-common-core";
import { Banner as BannerV2, BannerContentProps } from "@next-platform/client-common-spicy-ui";
import { memo, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { bannerVariantMap, PREVIEW_BANNER_KEY } from "@/constants";
import useLocalizedLocation from "@/hooks/useLocalizedLocation";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import usePlatform from "@/hooks/usePlatform";
import filterClosedBannersFromSession from "@/utils/filterClosedBannersFromSession";
import { getIsPartner } from "@/utils/getIsPartner";
import setClosedBannersSessionStorage from "@/utils/setClosedBannersInSessionStorage";

import CmsBannerService from "./CmsBannerService";
import { ActionType, IBannerProps, ICmsBannerProps } from "./types";

const Banner = ({
  className,
  isPreviewMode,
  customizedBanners,
  bannerVariant,
  shouldShowPopUpBanners,
  banners,
  onOpenChange,
  onUserViewedBanners,
  contentFulScreen,
}: IBannerProps) => {
  const refBanner = useRef<HTMLDivElement | null>(null);
  const reportViewedBanners = () => {
    if (isPreviewMode) {
      return;
    }
    onUserViewedBanners(banners, contentFulScreen);
  };
  const refReportViewedBanners = useRef(reportViewedBanners);
  refReportViewedBanners.current = reportViewedBanners;

  useEffect(() => {
    const bannerElement = refBanner.current;
    if (!bannerElement) {
      return undefined;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          refReportViewedBanners.current();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(bannerElement);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={refBanner} className={`grid ${className}`}>
      <BannerV2 data={customizedBanners} variant={bannerVariantMap[bannerVariant]} open={shouldShowPopUpBanners} onOpenChange={onOpenChange} />
    </div>
  );
};

const screensWithoutBackButton = [
  ContentFulScreens.SUBSCRIBE,
  ContentFulScreens.LEADERBOARD,
  ContentFulScreens.DOWNLOAD,
  ContentFulScreens.MY_CHATBOTS,
  ContentFulScreens.PERSONAS,
  ContentFulScreens.CREATE_CHATBOT,
  ContentFulScreens.MY_CHATS,
  ContentFulScreens.BLOCKED_CREATORS,
  ContentFulScreens.PROFILE,
];

const CmsBannerComponent = ({
  placement,
  className,
  shouldBeExcludedFromPagesWithTags = false,
  isVisibleOnPartner = false,
  isLayoutBanner = false,
  availableBanners,
  screen,
}: ICmsBannerProps) => {
  const [urlSearchParams] = useSearchParams();
  const isPreviewMode = !!urlSearchParams.get(PREVIEW_BANNER_KEY)?.length;

  const refBannersToBeCustomized = useRef<ICustomizedBanner[]>([]);
  const [stateCustomizedBanners, setStateCustomizedBanners] = useState<BannerContentProps[]>([]);
  const isPartner = getIsPartner();
  const { bannerDetails, onUserViewedBanners, onUserClosedBanner, getBannersToShow, onUserCTAClick } = useCms();
  const { pagePath } = useLocalizedLocation();
  const dispatch = useAppDispatch();
  const navigate = useLocalizedNavigation();
  const refNavigate = useRef(navigate);
  refNavigate.current = navigate;
  const [stateIsPopUpBannerOpen, setStateIsPopUpBannerOpen] = useState(true);
  const platform = usePlatform();
  useEffect(() => {
    if (placement !== BannerType.PopUp || !stateCustomizedBanners?.length) {
      return;
    }
    setStateIsPopUpBannerOpen(true);
  }, [pagePath, stateCustomizedBanners?.length, placement]);

  const shouldHideOnPartner = !isVisibleOnPartner && isPartner;
  const contentFulScreen = screen ?? CmsBannerService.contentfulScreen;
  const shouldHideOnPagesWithTag = shouldBeExcludedFromPagesWithTags && CmsBannerService.isPageExcludedForLayoutBanner(pagePath);
  const shouldHideBannerOnButtonWithBackButtons =
    placement === BannerType.Top && isLayoutBanner && !screensWithoutBackButton.includes(contentFulScreen! as ContentFulScreens);
  const shouldHideBanner =
    shouldHideOnPartner || shouldHideOnPagesWithTag || shouldHideBannerOnButtonWithBackButtons || !stateCustomizedBanners?.length;

  const closePopUpBanners = async () => {
    setStateIsPopUpBannerOpen(false);
    if (isPreviewMode) {
      return;
    }
    await onUserClosedBanner(refBannersToBeCustomized.current, contentFulScreen as ContentFulScreens, placement);
  };

  const onCTAClick = !isPreviewMode ? onUserCTAClick : () => {};

  const fetchBanners = async () => {
    if (!contentFulScreen) {
      return;
    }
    const banners = availableBanners ?? (await getBannersToShow(contentFulScreen, placement));
    const filteredBanners = filterClosedBannersFromSession(contentFulScreen, placement, banners);
    if (!filteredBanners?.length) {
      setStateCustomizedBanners([]);
      refBannersToBeCustomized.current = [];
      return;
    }
    const bannerService = new CmsBannerService({
      banners: filteredBanners,
      dispatch,
      navigate: (...args) => refNavigate.current(...args),
      onCTAClick,
      onPopUpCTAClick: closePopUpBanners,
      screen: contentFulScreen,
    });
    refBannersToBeCustomized.current = filteredBanners;
    setStateCustomizedBanners(bannerService.customizedBanners);
  };

  const onOpenChange = async (type: ActionType) => {
    if (type === ActionType.Dismiss && placement !== BannerType.PopUp) {
      const bannerNames = refBannersToBeCustomized.current?.filter((banner) => banner.closable).map((banner) => banner.name) || [];
      setClosedBannersSessionStorage({ bannerNames, screen: contentFulScreen as ContentFulScreens, placement });
      void fetchBanners();
      return;
    }
    await closePopUpBanners();
  };

  useEffect(() => {
    void fetchBanners();
  }, [bannerDetails, pagePath, screen]);
  if (shouldHideBanner || !contentFulScreen) {
    return null;
  }
  const shouldShowPopUpBanners = stateIsPopUpBannerOpen && placement === BannerType.PopUp;

  const shouldIncludeBanner = (platforms?: string[]) => {
    return isPreviewMode || platforms?.includes(platform);
  };
  const bannersToShow = (stateCustomizedBanners || []).filter((banner) => shouldIncludeBanner(banner.platforms));
  const filteredBanners = (refBannersToBeCustomized.current || []).filter((banner) => shouldIncludeBanner(banner?.platforms));
  if (bannersToShow.length === 0) {
    return null;
  }

  return (
    <Banner
      className={className}
      isPreviewMode={isPreviewMode}
      customizedBanners={bannersToShow}
      bannerVariant={placement}
      banners={filteredBanners}
      shouldShowPopUpBanners={shouldShowPopUpBanners}
      onOpenChange={() => {
        void onOpenChange(ActionType.Dismiss);
      }}
      onUserViewedBanners={onUserViewedBanners}
      contentFulScreen={contentFulScreen}
    />
  );
};

const CmsBanner = memo(CmsBannerComponent);

export default CmsBanner;
